import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

import Card from 'antd/es/card';
import Cascader from 'antd/es/cascader';

import ProcessTagsService from 'services/processTags';

import { Row, Col } from 'components/layout';
import Icon from 'components/Icon';
import { Text } from 'components/Text';
import { useFeatureToggleItems } from 'components/FeatureToggle';

import ContainerTimeline from 'components/process/ProcessTimeline';

import featureToggles from 'config/featureToggles';

import { formatDate, formatDuration } from 'helpers/date';

const StyledCard = styled(Card)`
  background-color: #FFFFFF;
  border: none;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);

  & .ant-card-body {
    display: flex;
    flex-direction: column;
  }
`;

const InfoItems = styled.div`
  display: flex;
  flex-direction: column;

  & > *:not(:last-child) {
    margin-bottom: 24px;
  }
`;

const StyledInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  & .name {
    font-weight: normal;
    font-size: 12px;
    line-height: 35px;
    color: #A4B0BA;
  }
`;

const PeopleCountTag = styled.div`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  
  width: 40px;
  height: 20px;
  padding: 4px 8px;
  
  background: #EFF2F5;
  border-radius: 32px;

  &::after {
    content: '${props => props.count || ''}';
    width: 8px;
    height: 12px;
    font-weight: normal;
    font-size: 12px;
    line-height: 100%;
    color: #45494D;
    margin: 0 3px;
  }
`;

// const TimeBufferTag = styled.div`
//   display: inline-flex;
//   flex-direction: row;
//   align-items: center;

//   width: fit-content;
//   height: 20px;
//   padding: 4px 8px;

//   background: #EFF2F5;
//   border-radius: 32px;

//   font-weight: normal;
//   font-size: 12px;
//   line-height: 100%;
//   color: #45494D;
// `;

const InfoItem = ({ label, children, ...rest }) => (
  <StyledInfoItem {...rest}>
    <Text className="name">{label}:</Text>
    {children}
  </StyledInfoItem>
);

const equipmentFieldLabels = {
  equipmentType: {
    CONTAINER: 'Container Type',
    TRAILER: 'Trailer Type',
  },
  method: {
    UNLOAD: 'Unload Method',
    LOAD: 'Load Method',
  },
  duration: {
    UNLOAD: 'Unload Time',
    LOAD: 'Load Time',
  },
  cartons: {
    UNLOAD: 'Cartons Unloaded',
    LOAD: 'Cartons Loaded',
  }
};

const processTagsService = new ProcessTagsService();

const EquipmentInfoCard = ({ container, containerTimeline, img, openCameraView, ...rest }) => {
  const [containerTags, setContainerTags] = useState([]);
  const containerFeatureToggle = useFeatureToggleItems(featureToggles.CONTAINER);
  
  useEffect(() => {
    if (container && container.process_id) {
      processTagsService.loadAll({ process_id: container.process_id, container_level: 1 })
        .then(data => {
          if (!data) return;
          setContainerTags(data.tags.map(tag => {
            const mapped = { ...tag };
            mapped.tag = mapped.tag.split('/');
            return mapped;
          }));
        });
    }
  }, [container]);

  const onContainerTagChange = useCallback((value, selectedOptions) => {
    let current = containerTags[0];

    if (value && value.length) {
      const tag = value.join('/');
      if (current) {
        // update
        processTagsService.update({
          ...current,
          tag,
        });
        current.tag = value;
        setContainerTags([current]);
      } else {
        // create
        current = {
          tag: value,
        };
        setContainerTags([current]);

        processTagsService.create({
          process_id: container.process_id,
          tag,
        }).then(data => {
          if (!data) return;
          const added = { ...data };
          added.tag = data.tag.split('/');
          setContainerTags([
            added,
          ]);
        });
      }
    } else {
      // delete
      if (containerTags.length) {
        const current = containerTags[0];
        if (!current || !current.id) return;
        processTagsService.remove(current);
        setContainerTags([]);
      }
    }
  }, [containerTags, container]);

  const [tagNameOptions] = useState(() => {
    return [
      {
        label: 'Training',
        value: 'training',
        children: [
          {
            label: 'In Training',
            value: 'in_training',
          },
          {
            label: 'Recently Trained',
            value: 'recently_trained',
          },
        ]
      },
      {
        label: 'Special Handling',
        value: 'special_handling',
        children: [
          { label: 'Mixed', value: 'mixed' },
          { label: 'Dunnage/Wrapped', value: 'dunnage_wrapped' },
          { label: 'Avalanche', value: 'avalanche' },
          { label: 'Non-Prime', value: 'non_prime' },
        ]
      },
      {
        label: 'Stopped',
        value: 'stopped',
      }
    ]
  });
  if (container === undefined || container === null) {
    container = {
      direction: 'unknown',
    };
  }
  const direction = container.direction || container.default_direction;
  const containerType = container.container_type?.toUpperCase();
  const isLowAccuracy = container.is_low_accuracy === 1;

  return (
    <StyledCard {...rest}>
      <Row style={{ paddingBottom: '12px' }} align="middle">
        <Col span={24}>
          <ContainerTimeline
            withStripes={isLowAccuracy}
            container={containerTimeline}
            openCameraView={openCameraView}
            style={{ height: '24px' }}
            size="small"
          />
        </Col>
      </Row>
      <Row align="stretch">
        <Col style={{
          paddingTop: '12px',
          paddingRight: '24px',
          flexBasis: '372px',
        }}>
          <Row justify="center" style={{ height: '76%' }}>
            <img
              alt="equipment vector"
              src={img}
            />
          </Row>
          <Row style={{ marginLeft: '8%' }}>
            <Col span={24} style={{ marginBottom: '8px' }}>
              <InfoItem label="Tag" />
            </Col>
            <Col span={24}>
              <Cascader
                value={containerTags && containerTags.length ? containerTags[0].tag : []}
                options={tagNameOptions}
                placeholder="Select Tag"
                allowClear={true}
                onChange={onContainerTagChange}
                style={{
                  width: '90%',
                }}
              />
            </Col>
          </Row>
        </Col>
        <Col flex="1" style={{ padding: '12px 0' }}>
          <InfoItems>
            {/* <InfoItem label="Equipment Code">{renderFieldValue(container, 'code')}</InfoItem> */}
            <InfoItem label="Customer">{renderFieldValue(container, 'customer_name')}</InfoItem>
            <InfoItem label={equipmentFieldLabels.equipmentType[containerType] || 'Type'}>{renderFieldValue(container, 'content_class')}</InfoItem>
            <InfoItem label={equipmentFieldLabels.method[direction] || 'Method'}>{renderFieldValue(container, 'unload_method')}</InfoItem>
            <InfoItem label="Volume">{renderFieldValue(container, 'volume')}</InfoItem>
            <InfoItem label="Weight">{renderFieldValue(container, 'weight')}</InfoItem>
            <InfoItem label="Splits">{renderFieldValue(container, 'splits')}</InfoItem>
          </InfoItems>
        </Col>
        <Col flex="1" style={{ padding: '12px 0' }}>
          <InfoItems>
            {containerFeatureToggle[0].visible &&<InfoItem label="Buffered target time">{renderFieldValue(container, 'target_time')}</InfoItem>}
            <InfoItem label="Target Time">{renderFieldValue(container, 'target_time0')}</InfoItem>
            <InfoItem label={equipmentFieldLabels.duration[direction] || 'Duration'}>{renderFieldValue(container, 'duration')}</InfoItem>
            <InfoItem label="Active Time">{renderFieldValue(container, 'active_time')}</InfoItem>
            <InfoItem label="Regular Breaks">{renderFieldValue(container, 'breaks_time')}</InfoItem>
            <InfoItem label="Extra Stop Time">{renderFieldValue(container, 'extra_stop_time')}</InfoItem>
            <InfoItem label="Total Idle Time">{renderFieldValue(container, 'idle_time')}</InfoItem>
          </InfoItems>
        </Col>
        <Col flex="1" style={{ padding: '12px 0' }}>
          <InfoItems>
            <InfoItem label={equipmentFieldLabels.cartons[direction] || 'Cartons'}>{renderFieldValue(container, 'cartons_report')}</InfoItem>
            <InfoItem label="Item Count">{renderFieldValue(container, 'distinct_item_count')}</InfoItem>
            <InfoItem label="Labeled">{renderFieldValue(container, 'cartons_labeled')}</InfoItem>
            {containerFeatureToggle[0].visible &&<InfoItem label="Buffered target CPH">{renderFieldValue(container, 'target_cph')}</InfoItem>}
            <InfoItem label="Target CPH">{renderFieldValue(container, 'target_cph0')}</InfoItem>
            <InfoItem label="Actual CPH">{renderFieldValue(container, 'avg_cph')}</InfoItem>
            <InfoItem label="Total man-min Detected">{renderFieldValue(container, 'labor_time')}</InfoItem>
          </InfoItems>
        </Col>
      </Row>
    </StyledCard>
  );
};

const renderFieldValue = (container, field) => {
  let isEst = false, isContentEst = false;
  let val = container[field];
  let extra = null;
  const dtFormat = 'MMM dd, yyyy \'at\' HH:mm';
  const dtOpts = { tzOffset: container.timezone_offset };
  const direction = container.direction || container.default_direction;
  const isContainer = direction === 'UNLOAD';
  const isForklift = (container.unload_method || '').toLowerCase() === 'clamp';

  switch (field) {
    case 'docked':
      if (container.docked_at) {
        val = formatDate(container.docked_at * 1000, dtFormat, dtOpts);
      }
    break;

    case 'undocked':
      if (typeof container.undocked_at) {
        val = formatDate(container.undocked_at * 1000, dtFormat, dtOpts);
      } else if (container.estimated_start) {
        val = formatDate(container.estimated_start * 1000, dtFormat, dtOpts);
        isEst = true;
      }
    break;

    case 'duration':
      if (typeof container.unload_duration === 'number') {
        val = formatDuration(container.unload_duration * 1000, false);
      } else if (typeof container.estimated_end === 'number') {
        const duration = (container.estimated_end - container.estimated_start) * 1000;
        val = formatDuration(duration, false);
        isEst = true;
      }
    break;

    case 'idle_time':
      const { dock_start_iddle, dock_end_iddle } = container;
      if (typeof dock_start_iddle === 'number' && typeof dock_end_iddle === 'number') {
        const idleTime = dock_start_iddle + dock_end_iddle;
        val = formatDuration(idleTime * 1000, false);
      }
    break;

    case 'labor_time':
      const { tot_labor_man_mins } = container;
      if (typeof tot_labor_man_mins === 'number') {
        if (isForklift) {
          val = 'N/A';
        } else {
          val = formatDuration(tot_labor_man_mins * 60 * 1000, false);
        }
      }
    break;

    case 'cartons_report':
      const { unloaded_boxes, cartons } = container;
      if (typeof cartons === 'number') {
        const boxes = typeof unloaded_boxes === 'number' ? `${unloaded_boxes}` : '-'
        val = `${boxes}/${cartons}`;
        isContentEst = container.content_estimated === 1;
      }
    break;

    case 'cartons_labeled':
      const { pct_labels } = container;
      if (typeof pct_labels === 'number') {
        val = Math.round(pct_labels * 100) + '%';
      }
    break;

    case 'target_time': {
      const { target_time } = container;
      if (container[field] === undefined) {
        val = '-';
        break;
      }
      val = !isContainer ? 'N/A'
      :
      target_time === 0 ? '-'
      : 
      typeof target_time === 'number' ?
      val = formatDuration(target_time * 3600 * 1000, false) : '-';

          // if (typeof pct_buffer_prediction === 'number') {
          //   extra = (
          //     <TimeBufferTag style={{ marginLeft: '4px' }}>
          //       {pct_buffer_prediction * 100}% buffer
          //     </TimeBufferTag>
          //   );
          // }
      }
    break;

    case 'target_time0': {
      const { target_time0 } = container;
      if (container[field] === undefined) {
        val = '-';
        break;
      }
      val = !isContainer ? 'N/A'
      :
      target_time0 === 0 ? '-'
      : 
      typeof target_time0 === 'number' ?
      val = formatDuration(target_time0 * 3600 * 1000, false) : '-';
      }
    break;
    
    case 'target_cph': {
      const { target_cph } = container;
      if (typeof target_cph === 'number' || target_cph === null) {
        if (!isContainer) {
          val = 'N/A';
        } else if (target_cph !== null) {
          val = Math.round(target_cph);
          // if (typeof pct_buffer_prediction === 'number') {
          //   extra = (
          //     <TimeBufferTag style={{ marginLeft: '4px' }}>
          //       {pct_buffer_prediction * 100}% buffer
          //     </TimeBufferTag>
          //   );
          // }
        }
      }
    }
    break;

    case 'target_cph0':
      const { target_cph0, expected_people } = container;
      if (typeof target_cph0 === 'number' || target_cph0 === null) {
        if (!isContainer) {
          val = 'N/A';
        } else if (target_cph0 !== null) {
          val = Math.round(target_cph0);
          if (typeof expected_people === 'number') {
            extra = (
              <PeopleCountTag count={expected_people} style={{ marginLeft: '4px' }}>
                <Icon name="users" size={12} color={'#B9C2C9'} />
              </PeopleCountTag>
            )
          }
        }
      }
    break;

    case 'avg_cph':
      const { cph } = container;
      if (typeof cph === 'number') {
        val = Math.round(cph);
      }
    break;

    case 'volume':
      const { volume } = container;
      if (typeof volume === 'number') {
        val = `${volume} cbm`;
        isContentEst = container.content_estimated === 1;
      }
    break;

    case 'weight':
      const { weight } = container;
      if (typeof weight === 'number') {
        val = `${weight} kg`;
        isContentEst = container.content_estimated === 1;
      }
    break;

    case 'total_time':
      const { docked_at, undocked_at, estimated_undock, total_time } = container;
      if (typeof total_time === 'number') {
        val = formatDuration(total_time * 60 * 1000, false);
      } else if (undocked_at) {
        const time = undocked_at - docked_at;
        val = formatDuration(time * 1000, false);
      } else if (estimated_undock) {
        const time = Math.abs(estimated_undock - docked_at);
        val = formatDuration(time * 1000, false);
        isEst = true;
      }
    break;

    case 'active_time':
      const { active_time } = container;
      if (typeof active_time === 'number') {
        if (isForklift) {
          val = 'N/A';
        } else {
          val = formatDuration(active_time * 1000, false);
        }
      }
    break;

    case 'breaks_time':
      const { breaks_time } = container;
      if (typeof breaks_time === 'number') {
        val = formatDuration(breaks_time * 1000, false);
      }
    break;

    case 'extra_stop_time':
      const { extra_stop_time } = container;
      if (typeof extra_stop_time === 'number') {
        val = formatDuration(extra_stop_time * 1000, false);
      }
    break;

    case 'splits':
      const { splits } = container;
      if (typeof splits === 'number') {
        val = `${splits}`;
        isContentEst = container.content_estimated === 1;
      }
      break;

    default:
      val = container[field];
  }

  return (
    <Text
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontWeight: 'normal',
        fontSize: '14px',
        lineHeight: '22px',
        color: isEst ? '#A4B0BA' : '#232526'
      }}
    >
      {val ? (isEst ? `Estimated. ${val}` : val) : '-'}
      {extra}
      {isContentEst ? (
        <Icon
          name="alert-circle"
          size={14}
          color="#F5222D"
          style={{ marginLeft: '6px' }}
        />
      ) : null}
    </Text>
  );
};

export default EquipmentInfoCard;
